<template>
  <div class="rso-page">
    <div class="rso-page-header">
      <h1 class="rso-page-title">НДС в материалах УСП</h1>
    </div>

    <div class="rso-filters-card">
      <div class="rso-filters-card__body">
        <div class="rso-filters-toolbar">
          <div class="rso-filters-main">
            <CustomSelect
              :options="residentOptions"
              option-label-key="resident_name"
              option-value-key="resident_id"
              label="Жилой комплекс"
              :value="residentId"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!residentId"
              class="rso-filter-field"
              @change="onResidentChange"
            />
            <CustomSelect
              :options="projectTypeOptions"
              option-value-key="value"
              option-label-key="label"
              label="Тип проекта"
              :value="projectTypeFilter"
              fullWidth
              :useInput="false"
              class="rso-filter-field"
              @change="onProjectTypeChange"
            />
            <CustomSelect
              :options="blockFilterOptions"
              option-value-key="value"
              option-label-key="label"
              label="Блок"
              clearable
              :disable="!residentId"
              :loading="isBlockOptionsLoading"
              :value="blockFilter"
              fullWidth
              :useInput="false"
              class="rso-filter-field"
              @change="onBlockChange"
            />
            <CustomSelect
              :options="floorFilterOptions"
              option-value-key="value"
              option-label-key="label"
              label="Этаж"
              clearable
              :disable="!residentId"
              :loading="isFloorOptionsLoading"
              :value="floorFilter"
              fullWidth
              :useInput="false"
              class="rso-filter-field"
              @change="onFloorChange"
            />
          </div>

          <div class="rso-filters-actions">
            <q-btn
              class="rso-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!residentId || !hasActiveFilters"
              @click="resetFilters"
            />
            <q-btn
              class="rso-filter-action-btn"
              :outline="!isAdvancedFiltersOpen"
              :unelevated="isAdvancedFiltersOpen"
              :color="advancedFiltersBtnColor"
              no-caps
              dense
              :icon-right="isAdvancedFiltersOpen ? 'expand_less' : 'expand_more'"
              label="Фильтры"
              :disable="!residentId"
              @click="isAdvancedFiltersOpen = !isAdvancedFiltersOpen"
            >
              <q-badge
                v-if="hasActiveAdvancedFilters"
                color="orange"
                floating
                rounded
              />
            </q-btn>
            <q-btn
              class="rso-filter-action-btn"
              color="positive"
              unelevated
              no-caps
              dense
              icon="download"
              label="Excel"
              :disable="!residentId || isExporting"
              :loading="isExporting"
              @click="exportExcel"
            />
          </div>
        </div>

        <div v-show="isAdvancedFiltersOpen" class="rso-filters-advanced">
          <CustomSelect
            :options="konstruktivFilterOptions"
            option-value-key="value"
            option-label-key="label"
            label="Конструктив"
            clearable
            :disable="!residentId"
            :value="konstruktivFilter"
            fullWidth
            :useInput="false"
            class="rso-filter-field"
            @change="(v) => (konstruktivFilter = v)"
          />
          <CustomSelect
            :options="materialFilterOptions"
            option-value-key="value"
            option-label-key="label"
            label="Материал"
            clearable
            :disable="!residentId"
            :value="materialFilter"
            fullWidth
            :useInput="false"
            class="rso-filter-field"
            @change="(v) => (materialFilter = v)"
          />
        </div>
      </div>
    </div>

    <q-linear-progress v-if="isFetching" indeterminate class="rso-progress" />

    <div v-if="!residentId" class="rso-hint">
      Выберите жилой комплекс, чтобы загрузить материалы команды проекта и подрядчика по структуре УСП.
    </div>

    <NotFoundText v-else-if="!isFetching && !treeRoots.length" />

    <div v-else-if="treeRoots.length" class="rso-tree-card q-mt-md">
      <div class="rso-tree-toolbar">
        <div class="rso-tree-toolbar__actions">
          <q-btn
            class="rso-tree-action-btn"
            flat
            dense
            no-caps
            size="sm"
            :icon-right="isTreeFullyExpanded ? 'unfold_less' : 'unfold_more'"
            :label="isTreeFullyExpanded ? 'Свернуть всё' : 'Развернуть всё'"
            :disable="isFetching"
            @click="toggleExpandAll"
          />
        </div>
        <div v-if="grandTotals" class="rso-tree-toolbar__total">
          <span class="rso-tree-toolbar__total-label">Итого:</span>
          <span class="rso-tree-toolbar__total-item">
            <span class="rso-tree-toolbar__total-suffix">с НДС</span>
            <Amount :value="grandTotals.sum_with_nds" :round="2" class="rso-tree-toolbar__total-value" />
          </span>
          <span class="rso-tree-toolbar__total-divider" aria-hidden="true" />
          <span class="rso-tree-toolbar__total-item">
            <span class="rso-tree-toolbar__total-suffix">без НДС</span>
            <Amount :value="grandTotals.sum_without_nds" :round="2" class="rso-tree-toolbar__total-value" />
          </span>
          <span class="rso-tree-toolbar__total-divider" aria-hidden="true" />
          <span class="rso-tree-toolbar__total-item">
            <span class="rso-tree-toolbar__total-suffix">НДС</span>
            <Amount :value="grandTotals.nds_amount" :round="2" class="rso-tree-toolbar__total-value" />
          </span>
        </div>
      </div>
      <div class="rso-tree-scroll">
        <div
          class="rso-tree-grid"
          :style="{ minWidth: `${USP_MATERIALS_NDS_GRID_MIN_WIDTH_PX}px` }"
        >
          <div class="rso-grid-header-sticky">
            <table class="rso-mini-table rso-smeta-grid-table" lang="ru">
              <colgroup>
                <col
                  v-for="c in USP_MATERIALS_NDS_GRID_COLS"
                  :key="c.key"
                  :style="{ width: c.width }"
                />
              </colgroup>
              <GridHeader />
            </table>
          </div>
          <TreeNode
            v-for="root in treeRoots"
            :key="root.id"
            :node="root"
            :depth="0"
            :expanded-ids="expandedIdsSet"
            @toggle="onToggleExpand"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import CustomSelect from "@/components/form/CustomSelect";
import NotFoundText from "@/components/common/NotFoundText";
import Amount from "@/components/common/Amount.vue";
import GridHeader from "./GridHeader.vue";
import TreeNode from "./TreeNode.vue";
import {
  USP_MATERIALS_NDS_GRID_COLS,
  USP_MATERIALS_NDS_GRID_MIN_WIDTH_PX,
} from "./gridColumns.js";
import {
  buildFilterOptionsFromRows,
  buildUspMaterialsNdsTree,
  collectExpandableIds,
  isFilterActive,
} from "@/_helpers/residentUspMaterialsNdsTree.js";
import {
  exportUspMaterialsNdsReport,
  fetchUspMaterialsNdsRaw,
  getResidentSmetaBlockOptions,
  getResidentSmetaFloorOptions,
} from "@/views/ProjectsEntry/services/index.js";
import { getResidentList, getProjectTypes } from "@/views/ResidentialSettings/services/index.js";

const residentId = ref(null);
const projectTypeFilter = ref(null);
const blockFilter = ref(null);
const floorFilter = ref(null);
const konstruktivFilter = ref(null);
const materialFilter = ref(null);

const residents = ref([]);
const projectTypes = ref([]);
const blockOptions = ref([]);
const floorOptions = ref([]);
const isBlockOptionsLoading = ref(false);
const isFloorOptionsLoading = ref(false);
const isExporting = ref(false);
const isAdvancedFiltersOpen = ref(false);
const expandedIdsSet = ref(new Set());

const residentOptions = computed(() => residents.value || []);

const projectTypeOptions = computed(() => {
  const base = [{ label: "Все типы", value: null }];
  for (const pt of projectTypes.value || []) {
    base.push({
      label: pt.project_type_name || String(pt.project_type_id),
      value: pt.project_type_id,
    });
  }
  return base;
});

const blockFilterOptions = computed(() => [
  { label: "Все блоки", value: null },
  ...(blockOptions.value || []),
]);

const floorFilterOptions = computed(() => [
  { label: "Все этажи", value: null },
  ...(floorOptions.value || []),
]);

const hasActiveAdvancedFilters = computed(
  () => isFilterActive(konstruktivFilter.value) || isFilterActive(materialFilter.value)
);

const hasActiveFilters = computed(
  () =>
    isFilterActive(projectTypeFilter.value) ||
    isFilterActive(blockFilter.value) ||
    isFilterActive(floorFilter.value) ||
    hasActiveAdvancedFilters.value
);

const advancedFiltersBtnColor = computed(() =>
  isAdvancedFiltersOpen.value || hasActiveAdvancedFilters.value ? "primary" : "grey-7"
);

watch(hasActiveAdvancedFilters, (active) => {
  if (active) isAdvancedFiltersOpen.value = true;
});

async function loadResidents() {
  const list = await getResidentList();
  residents.value = Array.isArray(list) ? list : [];
}

async function loadProjectTypes(rid) {
  if (!rid) {
    projectTypes.value = [];
    return;
  }
  const list = await getProjectTypes(rid);
  projectTypes.value = Array.isArray(list) ? list : [];
}

async function loadBlockOptions() {
  const rid = residentId.value;
  if (!rid) {
    blockOptions.value = [];
    return;
  }
  isBlockOptionsLoading.value = true;
  blockOptions.value = await getResidentSmetaBlockOptions(rid, projectTypeFilter.value);
  isBlockOptionsLoading.value = false;
  await loadFloorOptions();
}

async function loadFloorOptions() {
  const rid = residentId.value;
  if (!rid) {
    floorOptions.value = [];
    return;
  }
  isFloorOptionsLoading.value = true;
  const entranceIds = blockFilter.value
    ? null
    : (blockOptions.value || []).map((item) => item.value).filter(Boolean);
  floorOptions.value = await getResidentSmetaFloorOptions({
    resident_id: rid,
    project_type_id: projectTypeFilter.value,
    project_entrance_id: blockFilter.value,
    project_entrance_ids: entranceIds,
  });
  isFloorOptionsLoading.value = false;
}

function onResidentChange(v) {
  residentId.value = v;
  projectTypeFilter.value = null;
}

function onProjectTypeChange(v) {
  projectTypeFilter.value = v;
}

function onBlockChange(v) {
  blockFilter.value = v;
}

function onFloorChange(v) {
  floorFilter.value = v;
}

function resetFilters() {
  projectTypeFilter.value = null;
  blockFilter.value = null;
  floorFilter.value = null;
  konstruktivFilter.value = null;
  materialFilter.value = null;
  expandedIdsSet.value = new Set();
  if (residentId.value) loadBlockOptions();
}

watch(
  () => residentId.value,
  (rid) => {
    loadProjectTypes(rid);
    blockFilter.value = null;
    floorFilter.value = null;
    konstruktivFilter.value = null;
    materialFilter.value = null;
    blockOptions.value = [];
    floorOptions.value = [];
    expandedIdsSet.value = new Set();
    if (rid) loadBlockOptions();
  },
  { immediate: true }
);

watch(
  () => projectTypeFilter.value,
  () => {
    blockFilter.value = null;
    floorFilter.value = null;
    if (residentId.value) loadBlockOptions();
  }
);

watch(
  () => blockFilter.value,
  () => {
    floorFilter.value = null;
    if (residentId.value) loadFloorOptions();
  }
);

const queryKey = computed(() => [
  "residentUspMaterialsNds",
  residentId.value,
  projectTypeFilter.value,
  blockFilter.value,
  floorFilter.value,
]);

const { data: queryData, isFetching } = useQuery({
  queryKey,
  queryFn: async () => {
    const rid = residentId.value;
    if (!rid) return { raw: [] };
    return {
      raw: await fetchUspMaterialsNdsRaw({
        resident_id: rid,
        project_type_id: projectTypeFilter.value,
        project_entrance_id: blockFilter.value,
        floor_map_id: floorFilter.value,
      }),
    };
  },
  enabled: computed(() => !!residentId.value),
});

const rawRows = computed(() => queryData.value?.raw || []);

const filteredRows = computed(() => {
  const kName = konstruktivFilter.value;
  const mid = materialFilter.value;
  return rawRows.value.filter((row) => {
    if (isFilterActive(kName) && (row.konstruktiv_name || "Без привязки УСП") !== kName) {
      return false;
    }
    if (isFilterActive(mid) && Number(row.material_id) !== Number(mid)) {
      return false;
    }
    return true;
  });
});

const treeRoots = computed(() => {
  const rid = residentId.value;
  if (!rid) return [];
  const resName =
    residents.value.find((r) => r.resident_id === rid)?.resident_name ||
    filteredRows.value[0]?.resident_name ||
    "";
  return buildUspMaterialsNdsTree(filteredRows.value, resName);
});

const filterOptions = computed(() => buildFilterOptionsFromRows(rawRows.value));

const konstruktivFilterOptions = computed(() => [
  { label: "Все конструктивы", value: null },
  ...filterOptions.value.konstruktivs,
]);

const materialFilterOptions = computed(() => [
  { label: "Все материалы", value: null },
  ...filterOptions.value.materials,
]);

const allExpandableIds = computed(() => collectExpandableIds(treeRoots.value));

const isTreeFullyExpanded = computed(() => {
  const ids = allExpandableIds.value;
  if (!ids.length) return false;
  return ids.every((id) => expandedIdsSet.value.has(id));
});

const grandTotals = computed(() => {
  const root = treeRoots.value?.[0];
  if (!root) return null;
  return {
    sum_with_nds: root.sum_with_nds,
    sum_without_nds: root.sum_without_nds,
    nds_amount: root.nds_amount,
  };
});

function onToggleExpand(id) {
  const next = new Set(expandedIdsSet.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedIdsSet.value = next;
}

function toggleExpandAll() {
  if (isTreeFullyExpanded.value) expandedIdsSet.value = new Set();
  else expandedIdsSet.value = new Set(collectExpandableIds(treeRoots.value));
}

async function exportExcel() {
  const rid = residentId.value;
  if (!rid || isExporting.value) return;
  isExporting.value = true;
  try {
    await exportUspMaterialsNdsReport({
      resident_id: rid,
      project_type_id: projectTypeFilter.value,
      project_entrance_id: blockFilter.value,
      floor_map_id: floorFilter.value,
      konstruktiv_name: konstruktivFilter.value,
      material_id: materialFilter.value,
    });
  } finally {
    isExporting.value = false;
  }
}

loadResidents();
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.rso-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
}

.rso-page-header {
  margin-bottom: 8px;
}

.rso-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.rso-filters-card {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.rso-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.rso-filters-main {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.rso-filters-advanced {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e8edf3;
}

.rso-filters-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.rso-filter-field {
  min-width: 0;
  width: 100%;
}

.rso-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 118px;
  padding: 0 8px;
}

.rso-progress {
  margin-bottom: 8px;
}

.rso-hint {
  padding: 32px 0;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

.rso-tree-card {
  border: 1px solid $gray-light;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.rso-tree-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
}

.rso-tree-toolbar__total {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-left: auto;
}

.rso-tree-toolbar__total-label,
.rso-tree-toolbar__total-suffix {
  font-size: 12px;
  color: #475569;
}

.rso-tree-toolbar__total-item {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.rso-tree-toolbar__total-value {
  font-size: 14px;
  font-weight: 700;
  color: #16a34a;
}

.rso-tree-toolbar__total-divider {
  width: 1px;
  height: 18px;
  background: #cbd5e1;
  align-self: center;
}

.rso-tree-action-btn {
  font-size: 11px;
  color: #334155;
}

.rso-tree-scroll {
  overflow: auto;
  max-height: calc(100vh - 120px);
}

.rso-grid-header-sticky {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #eef4fb;
}

.rso-tree-card :deep(.rso-smeta-grid-table) {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rso-tree-card :deep(.rso-smeta-grid-table th),
.rso-tree-card :deep(.rso-smeta-grid-table td) {
  border: 1px solid #b8cfe4;
  padding: 3px 6px;
}

.rso-tree-card :deep(.rso-smeta-grid-table thead th) {
  background: #e9f1fb;
  font-weight: 600;
  color: #1e40af;
  text-align: center;
  padding: 8px 6px;
  font-size: 11px;
}

@media (max-width: 1280px) {
  .rso-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .rso-filters-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .rso-filters-main,
  .rso-filters-advanced {
    grid-template-columns: 1fr;
  }
}
</style>
